// M03 /diagnosticos/:id/painel: the dimension score, one two-needle gauge per subdimension, numeric values and
// deltas in percentage points, with a drill into categories. Missing scenarios stay visibly missing.
import { useQuery } from "@tanstack/react-query";
import { ErrorState, Panel, Pill, Skeleton, cx } from "../../components/ui.tsx";
import { DualGauge, GaugeLegend } from "../../components/maturity/gauge.tsx";
import { RadarChart } from "../../components/maturity/radar.tsx";
import { get } from "../../lib/api.ts";
import { useUrlState } from "../../lib/nav.tsx";
import { PROGNOSIS_NOTE, mBase, mKey, type AssessmentDetail, type DashboardData, type MaturityInstitution } from "../../lib/maturity.ts";
import { AssessmentShell } from "./AssessmentShell.tsx";
import { WithInstitution } from "./MaturityLayout.tsx";
import { formatDelta, formatPercent, type ScoreSummary } from "../../../shared/maturity/scoring.ts";
import type { CompareNode, CompareTree } from "../../../shared/maturity/compare.ts";
import { RADAR_PENDING_NOTE, radarAxes, radarSupported, withoutCode } from "../../../shared/maturity/radar.ts";

type View = "diagnosis" | "prognosis" | "comparison";
const VIEWS: { key: View; label: string }[] = [
  { key: "diagnosis", label: "Como estamos" },
  { key: "prognosis", label: "Como poderemos ficar" },
  { key: "comparison", label: "Comparação" },
];

export function MaturityDashboardPage({ id }: { id: string }) {
  return (
    <WithInstitution>
      {(inst) => <AssessmentShell institution={inst} id={id} tab="painel" title="Painel">{(a) => <DashboardBody institution={inst} assessment={a} />}</AssessmentShell>}
    </WithInstitution>
  );
}

const value = (s: ScoreSummary | null, missing: string) => (s?.fraction ? formatPercent(s) : s ? "Pendente" : missing);
const deltaText = (n: CompareNode) => {
  const d = formatDelta(n.delta);
  return d ? `${d} pontos percentuais` : null;
};

function DashboardBody({ institution, assessment: a }: { institution: MaturityInstitution; assessment: AssessmentDetail }) {
  const q = useQuery({
    queryKey: mKey(institution.id, "assessment", a.id, "dashboard"),
    queryFn: async () => (await get<{ data: DashboardData }>(`${mBase(institution.id)}/assessments/${a.id}/dashboard`)).data,
  });
  const [url, setUrl] = useUrlState({ ver: a.scenarios.prognosis ? "comparison" : "diagnosis", sub: "" });
  if (q.isPending) return <Skeleton lines={8} />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const d = q.data;
  const view = (VIEWS.some((v) => v.key === url.ver) ? url.ver : "comparison") as View;
  const showD = view !== "prognosis";
  const showP = view !== "diagnosis";
  const t = d.tree;
  const selected = t.subdimensions.find((s) => s.id === url.sub) ?? null;
  const draftNote = [d.diagnosis?.state === "draft" && "diagnóstico", d.prognosis?.state === "draft" && "prognóstico"].filter(Boolean).join(" e ");

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div role="radiogroup" aria-label="Visão do painel" className="flex w-full rounded-ctl border border-line bg-surface p-1 shadow-e1 sm:inline-flex sm:w-auto">
          {VIEWS.map((v) => (
            <button key={v.key} type="button" role="radio" aria-checked={view === v.key} onClick={() => setUrl({ ver: v.key })}
              className={cx("min-h-9 flex-1 rounded-[8px] px-2 text-[13px] leading-tight font-semibold sm:flex-none sm:px-3 sm:text-sm transition-colors duration-[var(--duration-base)]", view === v.key ? "bg-primary text-white" : "text-muted hover:bg-paper hover:text-ink")}>
              {v.label}
            </button>
          ))}
        </div>
        <GaugeLegend showDiagnosis={showD} showPrognosis={showP} />
      </div>
      {draftNote && <p className="mb-3 text-sm text-muted"><Pill tone="gold">Rascunho</Pill> Valores do {draftNote} calculados com as respostas salvas até agora.</p>}
      {showP && <p className="mb-4 text-sm text-muted">{PROGNOSIS_NOTE}{!d.prognosis && " Sem prognóstico registrado."}</p>}

      <Panel className="mb-6 flex flex-wrap items-center gap-6">
        <DualGauge diagnosis={t.diagnosis} prognosis={t.prognosis} showDiagnosis={showD} showPrognosis={showP} label={t.title} size={220} />
        <div className="min-w-[min(100%,240px)] flex-1">
          <p className="eyebrow">{t.title} · nota geral</p>
          <Values node={t} showD={showD} showP={showP} big />
          <p className="mt-2 text-xs text-ink-3">Média simples das {t.subdimensions.length} subdimensões; cada subdimensão é a média simples das suas categorias.</p>
        </div>
      </Panel>

      <RadarPanel tree={t} showD={showD} showP={showP} />

      <h2 className="mb-3 text-lg font-bold">Subdimensões</h2>
      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {t.subdimensions.map((s) => (
          <li key={s.id}>
            <button type="button" onClick={() => setUrl({ sub: s.id === url.sub ? "" : s.id })} aria-expanded={s.id === url.sub}
              aria-controls="categorias" className={cx("lift flex h-full w-full flex-col rounded-panel border bg-surface p-4 text-left", s.id === url.sub ? "border-accent ring-2 ring-accent/30" : "border-line")}>
              <span className="text-sm leading-snug font-semibold">{s.title}</span>
              <span className="mt-2 self-center"><DualGauge diagnosis={s.diagnosis} prognosis={s.prognosis} showDiagnosis={showD} showPrognosis={showP} label={s.title} size={170} /></span>
              <Values node={s} showD={showD} showP={showP} />
              <span className="mt-auto pt-2 text-xs font-semibold text-primary">{s.id === url.sub ? "Ocultar categorias" : "Ver categorias"}</span>
            </button>
          </li>
        ))}
      </ul>

      {selected && (
        <Panel className="mt-6" as="section">
          <h3 id="categorias" className="text-[17px] font-semibold">{selected.title}</h3>
          <p className="text-sm text-muted">Categorias e percentuais. Uma categoria com quesito sem resposta fica pendente.</p>
          <div className="mt-3 hidden overflow-hidden rounded-ctl border border-line md:block">
            <table className="w-full text-sm">
              <thead className="bg-paper text-left">
                <tr>
                  <th className="eyebrow px-3 py-2.5">Categoria</th>
                  {showD && <th className="eyebrow px-3 py-2.5 text-right">Diagnóstico</th>}
                  {showP && <th className="eyebrow px-3 py-2.5 text-right">Prognóstico</th>}
                  {showD && showP && <th className="eyebrow px-3 py-2.5 text-right">Diferença</th>}
                  <th className="eyebrow px-3 py-2.5">Situação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {selected.categories.map((c) => (
                  <tr key={c.id} className="hover:bg-paper/70">
                    <td className="px-3 py-2.5">{c.title}</td>
                    {showD && <td className="num px-3 py-2.5 text-right text-[13px]">{value(c.diagnosis, "—")}</td>}
                    {showP && <td className="num px-3 py-2.5 text-right text-[13px]">{value(c.prognosis, "Sem prognóstico")}</td>}
                    {showD && showP && <td className="num px-3 py-2.5 text-right text-[13px]">{formatDelta(c.delta) ?? "—"}</td>}
                    <td className="px-3 py-2.5 text-xs text-muted">{situationText(c, showD, showP)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="mt-3 space-y-2 md:hidden">
            {selected.categories.map((c) => (
              <li key={c.id} className="rounded-ctl border border-line p-3">
                <p className="text-sm font-semibold">{c.title}</p>
                <Values node={c} showD={showD} showP={showP} />
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}

function RadarPanel({ tree: t, showD, showP }: { tree: CompareTree; showD: boolean; showP: boolean }) {
  if (!radarSupported(t.subdimensions)) return null;
  const axes = radarAxes(t);
  const incomplete = axes.some((a) => (showD && a.diagnosis === null) || (showP && a.prognosis === null));
  return (
    <Panel className="mb-6" as="section">
      <h2 className="text-lg font-bold">Radar por subdimensão</h2>
      <p className="text-sm text-muted">Cada eixo é uma subdimensão (0 no centro, 100% na borda).{incomplete && ` ${RADAR_PENDING_NOTE}`}</p>
      <div className="mt-3 grid items-center gap-5 lg:grid-cols-[minmax(0,420px)_1fr]">
        <div className="justify-self-center"><RadarChart axes={axes} showDiagnosis={showD} showPrognosis={showP} label={`Radar de ${t.title} por subdimensão`} /></div>
        <div className="overflow-x-auto rounded-ctl border border-line">
          <table className="w-full text-sm">
            <thead className="bg-paper text-left">
              <tr>
                <th className="eyebrow px-3 py-2.5">Subdimensão</th>
                {showD && <th className="eyebrow px-3 py-2.5 text-right">Diag.</th>}
                {showP && <th className="eyebrow px-3 py-2.5 text-right">Prog.</th>}
                {showD && showP && <th className="eyebrow px-3 py-2.5 text-right">Dif.</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {t.subdimensions.map((s) => (
                <tr key={s.id} className="hover:bg-paper/70">
                  <td className="px-3 py-2" title={s.title}><span className="num mr-1.5 text-xs font-semibold">{s.code}</span><span className="hidden sm:inline">{withoutCode(s)}</span></td>
                  {showD && <td className="num px-3 py-2 text-right text-[13px]">{value(s.diagnosis, "—")}</td>}
                  {showP && <td className="num px-3 py-2 text-right text-[13px]">{value(s.prognosis, "—")}</td>}
                  {showD && showP && <td className="num px-3 py-2 text-right text-[13px]">{formatDelta(s.delta) ?? "—"}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Panel>
  );
}

function situationText(n: CompareNode, showD: boolean, showP: boolean) {
  const part = (s: ScoreSummary | null, label: string) => (s ? (s.fraction ? `${label}: ${s.adoption} · ${s.situation}` : `${label}: pendente (${s.answered} de ${s.total})`) : `${label}: sem registro`);
  return [showD && part(n.diagnosis, "Diag."), showP && part(n.prognosis, "Prog.")].filter(Boolean).join(" — ");
}

function Values({ node: n, showD, showP, big }: { node: CompareNode; showD: boolean; showP: boolean; big?: boolean }) {
  const delta = deltaText(n);
  return (
    <dl className={cx("mt-2 grid gap-1.5", big ? "text-base" : "text-sm")}>
      {showD && (
        <div className="flex flex-wrap items-baseline gap-x-2">
          <dt className="text-muted">Diagnóstico</dt>
          <dd className={cx("num min-w-0 font-medium [overflow-wrap:anywhere]", big && n.diagnosis?.fraction && "text-2xl")}>{value(n.diagnosis, "—")}</dd>
          {n.diagnosis?.fraction && <dd className="text-xs text-muted">{n.diagnosis.adoption} · {n.diagnosis.situation}</dd>}
        </div>
      )}
      {showP && (
        <div className="flex flex-wrap items-baseline gap-x-2">
          <dt className="text-muted">Prognóstico</dt>
          <dd className={cx("num min-w-0 font-medium [overflow-wrap:anywhere]", big && n.prognosis?.fraction && "text-2xl")}>{value(n.prognosis, "Sem prognóstico registrado")}</dd>
          {n.prognosis?.fraction && <dd className="text-xs text-muted">{n.prognosis.adoption} · {n.prognosis.situation}</dd>}
        </div>
      )}
      {showD && showP && <div className="text-xs"><dt className="sr-only">Diferença</dt><dd className="num">{delta ? `Diferença: ${delta}` : "Diferença: indisponível"}</dd></div>}
    </dl>
  );
}
